import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  useTheme,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  Category as CategoryIcon,
  Description as DescriptionIcon,
  CheckCircle,
  ExpandMore as ExpandMoreIcon,
  AutoFixHigh as PolishIcon,
  Source as SourceIcon,
  Label as TypeIcon
} from '@mui/icons-material';
import type { SemanticFramework } from '../services/semanticService';

interface SemanticFrameworkResultsProps {
  framework: SemanticFramework | null;
  isLoading?: boolean;
  workflowId?: string;
  clientId?: string;
}

const SemanticFrameworkResults: React.FC<SemanticFrameworkResultsProps> = ({
  framework,
  isLoading = false,
  workflowId,
  clientId,
}) => {
  const theme = useTheme();
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading semantic framework...</Typography>
      </Box>
    );
  }

  if (!framework) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body1" align="center">
          No semantic framework available. Configure and start an analysis to see results.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <Typography variant="h6" component="div">
                Semantic Analysis Complete
              </Typography>
              <Chip 
                label={`${framework.categories.length} Categories`}
                color="primary"
                size="small"
                icon={<CategoryIcon fontSize="small" />}
                sx={{ ml: 2 }}
              />
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Framework ID:</strong> {framework.framework_id}
              </Typography>
              {workflowId && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Workflow:</strong> {workflowId}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Polishing Status */}
      {framework.polishing_metadata && (
        <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PolishIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Framework Polished
              </Typography>
              <Typography variant="body2" sx={{ ml: 2 }}>
                Polished on {new Date(framework.polishing_metadata.polished_at).toLocaleDateString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Categories & Patterns */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Semantic Categories & Topics
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The semantic analysis identified the following categories from multiple sources, including polished names and subtopics.
        </Typography>
        
        {framework.categories.length > 0 ? (
          <Stack spacing={2}>
            {framework.categories.map((category, categoryIndex) => (
              <Accordion key={`cat-${categoryIndex}`} sx={{ 
                '&:before': { display: 'none' },
                boxShadow: 1,
                '&.Mui-expanded': {
                  boxShadow: 3,
                }
              }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'background.paper',
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    '&.Mui-expanded': {
                      bgcolor: `${theme.palette.primary.main}08`,
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                        flexGrow: 1
                      }}
                    >
                      {category.name}
                    </Typography>
                    
                    {/* Source Module Chip */}
                    <Tooltip title={`Source: ${category.source_module || 'module3'}`}>
                      <Chip
                        icon={<SourceIcon fontSize="small" />}
                        label={category.source_module || 'module3'}
                        size="small"
                        variant="outlined"
                        sx={{
                          mr: 1,
                          backgroundColor: category.source_module === 'module1' ? '#e3f2fd' :
                                         category.source_module === 'module3' ? '#f3e5f5' : '#e8f5e8',
                          borderColor: category.source_module === 'module1' ? '#2196f3' :
                                     category.source_module === 'module3' ? '#9c27b0' : '#4caf50',
                          color: category.source_module === 'module1' ? '#1976d2' :
                               category.source_module === 'module3' ? '#7b1fa2' : '#388e3c'
                        }}
                      />
                    </Tooltip>
                    
                    {/* Type Chip */}
                    <Tooltip title={`Type: ${category.type || 'Unknown'}`}>
                      <Chip
                        icon={<TypeIcon fontSize="small" />}
                        label={category.type || 'Unknown'}
                        size="small"
                        variant="outlined"
                        sx={{
                          backgroundColor: category.type === 'thematic' ? '#fff3e0' :
                                         category.type === 'semantic' ? '#f1f8e9' : '#fce4ec',
                          borderColor: category.type === 'thematic' ? '#ff9800' :
                                     category.type === 'semantic' ? '#8bc34a' : '#e91e63',
                          color: category.type === 'thematic' ? '#ef6c00' :
                               category.type === 'semantic' ? '#689f38' : '#c2185b'
                        }}
                      />
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 3, fontStyle: 'italic' }}
                  >
                    {category.definition}
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {/* Main Category Examples */}
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: theme.palette.text.primary,
                          mb: 1
                        }}
                      >
                        Category Examples:
                      </Typography>
                      {category.examples && category.examples.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {category.examples.map((example, exampleIndex) => (
                            <Chip
                              key={exampleIndex}
                              label={example}
                              size="small"
                              variant="outlined"
                              sx={{
                                mb: 0.5,
                                backgroundColor: `${theme.palette.primary.main}15`,
                                borderColor: theme.palette.primary.light,
                                color: theme.palette.primary.dark
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No examples available
                        </Typography>
                      )}
                    </Grid>
                    
                    {/* Subtopics */}
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: theme.palette.text.primary,
                          mb: 1
                        }}
                      >
                        Subtopics ({(category.subtopics || []).length}):
                      </Typography>
                      {category.subtopics && category.subtopics.length > 0 ? (
                        <Stack spacing={1}>
                          {category.subtopics.map((subtopic, subtopicIndex) => (
                            <Paper 
                              key={subtopicIndex} 
                              variant="outlined" 
                              sx={{ 
                                p: 1.5, 
                                bgcolor: 'background.default',
                                borderLeft: `3px solid ${theme.palette.secondary.main}`,
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                fontWeight="medium" 
                                sx={{ mb: 0.5, color: theme.palette.secondary.dark }}
                              >
                                {subtopic.name}
                              </Typography>
                              
                              {subtopic.definition && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  display="block"
                                  sx={{ mb: 1 }}
                                >
                                  {subtopic.definition}
                                </Typography>
                              )}
                              
                              {subtopic.examples && subtopic.examples.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {subtopic.examples.map((ex, i) => (
                                      <Chip
                                        key={i}
                                        label={ex}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: '20px',
                                          fontSize: '0.7rem',
                                          backgroundColor: `${theme.palette.secondary.main}10`,
                                          borderColor: theme.palette.secondary.light,
                                          color: theme.palette.secondary.dark
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No subtopics available
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  
                  {/* Patterns (if available) */}
                  {category.patterns && category.patterns.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: theme.palette.text.primary,
                          mb: 2
                        }}
                      >
                        Patterns:
                      </Typography>
                      <Grid container spacing={2}>
                        {category.patterns.map((pattern, patternIndex) => (
                          <Grid item xs={12} md={6} key={patternIndex}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1.5, 
                                bgcolor: 'background.paper',
                                borderLeft: `4px solid ${theme.palette.success.main}`,
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                fontWeight="medium" 
                                sx={{ mb: 0.5, color: theme.palette.success.dark }}
                              >
                                {pattern.pattern}
                              </Typography>
                              
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                display="block"
                                sx={{ mb: 1 }}
                              >
                                {pattern.description}
                              </Typography>
                              
                              {pattern.examples && pattern.examples.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {pattern.examples.map((ex, i) => (
                                      <Chip
                                        key={i}
                                        label={ex}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: '20px',
                                          fontSize: '0.7rem',
                                          backgroundColor: `${theme.palette.success.main}10`,
                                          borderColor: theme.palette.success.light,
                                          color: theme.palette.success.dark
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            No categories have been identified in this framework
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default SemanticFrameworkResults;
